import React, { useState, useEffect } from 'react';
import { CommonInput } from '@/components/ui/common-input';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSelect } from '@/components/ui/common-select';
import { useToast } from '@/components/ui/use-toast';
import MapSelector from '../addports/MapSelector';
import Loader from '@/components/ui/loader';

const EditPortForm = ({ port, onUpdatePort, onCancel, isSubmitting }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    port_name: '',
    port_code: '',
    porttype: '', 
    latitude: '',
    longitude: '',
    address: '',
    contact_person: '',
    phone_number: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [coordinates, setCoordinates] = useState({ lat: null, lng: null });
  const [loading, setLoading] = useState(false);


  const portTypeOptions = [
    { value: "Plant", label: "Plant" },
    { value: "Port", label: "Port" }
  ];

  useEffect(() => {
    if (port) {
      setLoading(true);
      setTimeout(() => {
        const lat = port.coordinates?.lat || port.latitude || '';
        const lng = port.coordinates?.lng || port.longitude || '';

        setFormData({
          port_name: port.name || '',
          port_code: port.code || '',
          porttype: port.porttype || '', 
          latitude: lat,
          longitude: lng,
          address: port.address || '',
          contact_person: port.contactPerson || '',
          phone_number: port.phoneNumber || '',
          is_active: port.status === 'Active'
        });

        if (lat && lng) {
          setCoordinates({ lat: parseFloat(lat), lng: parseFloat(lng) });
        }
        setLoading(false);
      }, 300);
    }
  }, [port]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.port_name.trim()) {
      newErrors.port_name = 'Port name is required';
    }

    if (!formData.port_code.trim()) {
      newErrors.port_code = 'Port code is required';
    }


    if (!formData.porttype) {
      newErrors.porttype = 'Port type is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.contact_person.trim()) {
      newErrors.contact_person = 'Contact person is required';
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required';
    } else if (formData.phone_number.length !== 10) {
      newErrors.phone_number = 'Phone number must be exactly 10 digits';
    } else if (!/^[0-9]{10}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone number must contain exactly 10 digits';
    }

    if (!formData.latitude || !formData.longitude) {
      newErrors.coordinates = 'Latitude and longitude are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleLocationSelect = (location) => {
    setCoordinates(location);
    setFormData(prev => ({
      ...prev,
      latitude: location.lat.toString(),
      longitude: location.lng.toString()
    }));

    if (errors.coordinates) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.coordinates;
        return newErrors;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const updatedPort = {
        id: port.id,
        port_name: formData.port_name,
        port_code: formData.port_code,
        porttype: formData.porttype,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        address: formData.address,
        contact_person: formData.contact_person,
        phone_number: formData.phone_number,
        is_active: formData.is_active
      };

      onUpdatePort(updatedPort);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader size="lg" message="Loading port details..." />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <MapSelector
            onLocationSelect={handleLocationSelect}
            initialLat={coordinates.lat}
            initialLng={coordinates.lng}
          />
          {errors.coordinates && (
            <p className="text-sm text-destructive mt-1">{errors.coordinates}</p>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonInput
              label="Port Name"
              id="port_name"
              value={formData.port_name}
              onChange={(e) => handleChange('port_name', e.target.value)}
              error={errors.port_name}
              required
              placeholder="Enter port name"
            />

            <CommonInput
              label="Port Code"
              id="port_code"
              value={formData.port_code}
              onChange={(e) => handleChange('port_code', e.target.value)}
              error={errors.port_code}
              required
              placeholder="Enter port code"
            />
          </div>
          
          <CommonSelect
            label="Port Type"
            id="porttype"
            value={formData.porttype}
            onChange={(value) => handleChange('porttype', value)}
            options={portTypeOptions}
            error={errors.porttype}
            required
            placeholder="Select Port Type"
            searchable
          />

          <CommonInput
            label="Address"
            id="address"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            error={errors.address}
            required
            placeholder="Enter full address"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonInput
              label="Contact Person"
              id="contact_person"
              value={formData.contact_person}
              onChange={(e) => handleChange('contact_person', e.target.value)}
              error={errors.contact_person}
              required
              placeholder="Enter contact person name"
            />

            <CommonInput
              label="Phone Number"
              id="phone_number"
              type="tel"
              value={formData.phone_number}
              onChange={(e) => {
                const inputValue = e.target.value;
             
                const sanitizedValue = inputValue.replace(/\D/g, '').slice(0, 10);
                handleChange('phone_number', sanitizedValue);
              }}
              onKeyPress={(e) => {
            
                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
              error={errors.phone_number}
              required
              placeholder="Enter 10-digit phone number"
              maxLength={10}
            />
          </div>
          <div className="flex justify-end space-x-2 pt-5">
            <CommonButton 
              type="button" 
              variant="outline" 
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancel
            </CommonButton>
            <CommonButton 
              type="submit" 
              variant="default"
              loading={isSubmitting}
            >
              Update Port
            </CommonButton>
          </div>
        </div>
      </div>
    </form>
  );
};

export default EditPortForm;